/**
 * Root application component.
 *
 * Sets up the provider hierarchy and router.
 * Provider order (outermost to innermost):
 *   1. ThemeProvider — MUI theme (BOLT-022)
 *   2. CssBaseline — normalize browser styles (MUI)
 *   3. LocalizationProvider — date/time adapter for @mui/x-date-pickers
 *   4. SnackbarProvider — notistack toast stacking (BOLT-022)
 *   5. QueryClientProvider — server state management (TanStack Query)
 *   6. BrowserRouter — client-side routing (React Router 8)
 *   7. ErrorBoundary — catches render errors
 *   8. AppRoutes — route definitions
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SnackbarProvider } from 'notistack';
import { BrowserRouter } from 'react-router';
import theme from './theme.js';
import { AppRoutes } from './routes.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { AuthBootstrap } from './components/auth/AuthBootstrap.js';
import { queryClient } from './lib/query-client.js';

export function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <ErrorBoundary>
                <AuthBootstrap>
                  <AppRoutes />
                </AuthBootstrap>
              </ErrorBoundary>
            </BrowserRouter>
          </QueryClientProvider>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
